/**
 * The first thing the page does (DLY-003): find out which table to play, showing a
 * loading line while it asks, and a plain message with one button if it cannot.
 *
 * Retry and Reload both reload the page. That is deliberate: a reload also sends the
 * player through the Access login again if their session ran out while the site is
 * private, and it fetches a new build if the level needs newer physics.
 */
import { TODAY_COPY } from '../config/site.ts';
import type { LabParams } from '../lab.ts';
import { archiveTable, chooseTable, type GameTable } from './table.ts';
import { fetchLevel, fetchToday, type TodaySource } from './today.ts';
import { clearArchiveDay, readArchiveDay } from '../calendar/archive-choice.ts';

export async function loadTable(app: HTMLElement, lab: LabParams): Promise<GameTable> {
  // Debug mode (local only, D-031) plays the table in the URL and never asks the server.
  if (lab.debug) {
    const table = chooseTable(lab, undefined, Date.now());
    if (table === undefined) throw new Error('loadTable: debug mode must always deal a table');
    return table;
  }

  app.innerHTML = `<p class="notice notice--loading" role="status">${TODAY_COPY.loading}</p>`;
  const source = await fetchToday();
  const table = chooseTable(lab, source, Date.now());
  if (table !== undefined) return archiveChoice(table);

  showProblem(app, source);
  /*
   * Nothing to play, and the message is on screen. Never settle, so nothing after the
   * `await` in main.ts runs: the only way on is the button, which reloads the page.
   */
  return new Promise<never>(() => undefined);
}

function showProblem(app: HTMLElement, source: TodaySource): void {
  const outOfDate = source.kind === 'out-of-date';
  app.innerHTML = `
    <main class="notice" role="alert">
      <h1 class="notice__title">${outOfDate ? TODAY_COPY.outOfDateTitle : TODAY_COPY.unavailableTitle}</h1>
      <p class="notice__body">${outOfDate ? TODAY_COPY.outOfDate : TODAY_COPY.unavailable}</p>
      <button type="button" class="btn btn--primary" data-reload>${
        outOfDate ? TODAY_COPY.reload : TODAY_COPY.retry
      }</button>
    </main>
  `;
  const button = app.querySelector<HTMLButtonElement>('[data-reload]');
  if (button === null) throw new Error('loadTable: the reload button is missing');
  button.addEventListener('click', () => {
    window.location.reload();
  });
  button.focus();
}

/**
 * The calendar's choice for this tab (DLY-005), if it is a past day the server will
 * give us. Anything else (no choice, today, a future or nonsense day, a day that will
 * not load) clears the choice and plays today's table, which is the daily game itself,
 * never a stand-in.
 */
async function archiveChoice(today: GameTable): Promise<GameTable> {
  const chosen = readArchiveDay();
  if (chosen === undefined) return today;
  if (today.mode !== 'daily' || today.todayKey === undefined || chosen >= today.todayKey) {
    clearArchiveDay();
    return today;
  }
  const past = await fetchLevel(chosen);
  if (past.kind !== 'level') {
    clearArchiveDay();
    return today;
  }
  return archiveTable(past.level, today);
}
